/**
 * Search tab — shared client-side kind metadata, condition describers, and
 * definition helpers. The server catalog (backend crmSearch/catalog.js) is the
 * validation authority; this mirrors just enough vocabulary to build editors
 * and readable summaries. Keep the kind list in lock-step with the catalog.
 *
 * Every sentence a recruiter reads about a search is written HERE — step rows,
 * funnel bars, drill-in titles, warnings — so the wording stays one voice
 * (docs/crm-outreach-lists-plan.md §A3–A6: the owner's complaint was raw keys,
 * symbol operators and analyst jargon on exactly these surfaces).
 */
import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type {
  CrmForm, ExportAttachPreflight, QuestionLabelInfo, SearchCondition, SearchDefinition, SearchStepDef, SearchStepStats,
  StudyOption, TeamMember,
} from '../../types';
import { studyInternalLabel } from '../../types';
import {
  DISPOSITION_REASONS, DQ_CATEGORIES, INCOMING_ELIGIBILITY_OPTIONS, LIFECYCLES, MILESTONE_LABELS, formSurface,
  prettySlug,
} from '../../lib';

export const KIND_CATEGORIES = [
  { key: 'pipeline', label: 'Pipeline & attribution' },
  { key: 'medical', label: 'Medical profile' },
  { key: 'forms', label: 'Forms & answers' },
  { key: 'eligibility', label: 'Eligibility & screening' },
  { key: 'engagement', label: 'Engagement & records' },
] as const;

export interface KindMeta {
  kind: string;
  label: string;
  category: (typeof KIND_CATEGORIES)[number]['key'];
  hint: string;
}

/** Palette entries (P1 + P2 catalog). Order within a category = display order. */
export const KINDS: KindMeta[] = [
  { kind: 'stage', label: 'Stage', category: 'pipeline', hint: 'Where they are in the pipeline right now' },
  { kind: 'stage_ever', label: 'Stage ever reached', category: 'pipeline', hint: 'Ever scheduled, or dropped out after qualifying…' },
  { kind: 'study', label: 'Study', category: 'pipeline', hint: 'The study they are being considered for, or any study they applied to' },
  { kind: 'no_study', label: 'No current study', category: 'pipeline', hint: 'Not being considered for any study — the people free for a new one' },
  { kind: 'owner', label: 'Owner', category: 'pipeline', hint: 'Which recruiter owns them (or nobody)' },
  { kind: 'incoming_eligibility', label: 'Incoming eligibility', category: 'pipeline', hint: 'What their landing-page answers said about eligibility' },
  { kind: 'milestone', label: 'Milestone', category: 'pipeline', hint: 'The progress line under their name' },
  { kind: 'lead_source', label: 'Lead source', category: 'pipeline', hint: 'Where they came from — Facebook, Instagram, an import…' },
  { kind: 'utm', label: 'Ad campaign', category: 'pipeline', hint: 'Which ad campaign, source or medium brought them in' },
  { kind: 'disposition', label: 'Closed out', category: 'pipeline', hint: 'Closed out — why, and on which study' },
  { kind: 'tag', label: 'Tag', category: 'pipeline', hint: 'Tags on the lead' },
  { kind: 'interest', label: 'Future-study list', category: 'pipeline', hint: 'Kept for a study type we regularly get (gout, migraine…)' },
  { kind: 'created', label: 'Created date', category: 'pipeline', hint: 'When they first came in' },
  { kind: 'activity', label: 'Activity recency', category: 'pipeline', hint: 'Contacted or heard from in the last N days — or not' },
  { kind: 'contactable', label: 'Contactable', category: 'pipeline', hint: 'Has a phone we may text, or an email we may send to' },
  { kind: 'snoozed', label: 'Snoozed', category: 'pipeline', hint: 'Snoozed right now, or not' },
  { kind: 'archived', label: 'Archived', category: 'pipeline', hint: 'Only useful when the starting group includes archived leads' },
  { kind: 'condition', label: 'Medical condition', category: 'medical', hint: 'A diagnosis on their profile — from a form, the AI, or a recruiter' },
  { kind: 'medication', label: 'Medication', category: 'medical', hint: 'A medication on their profile' },
  { kind: 'lab', label: 'Lab value', category: 'medical', hint: 'A lab result — A1C of 7 or more in the last year…' },
  { kind: 'vital', label: 'Vital', category: 'medical', hint: 'BMI, weight, height, blood pressure, heart rate' },
  { kind: 'demographic', label: 'Demographic', category: 'medical', hint: 'Age or sex — where a form asked or their records showed it' },
  { kind: 'form_submitted', label: 'Form submitted', category: 'forms', hint: 'Filled in a particular landing page or texted form' },
  { kind: 'answer', label: 'Answer', category: 'forms', hint: 'Gave a particular answer to a question' },
  { kind: 'answered', label: 'Answered question', category: 'forms', hint: 'Answered a question at all (any answer)' },
  { kind: 'study_eligibility', label: 'Study eligibility', category: 'eligibility', hint: 'Prescreen result for one study (eligible, not eligible, withdrew)' },
  { kind: 'ai_review', label: 'AI eligibility review', category: 'eligibility', hint: 'Result of the AI medical-records review' },
  { kind: 'dq', label: 'Disqualification', category: 'eligibility', hint: 'Screened out — why, on which study, or when' },
  { kind: 'communication', label: 'Communication volume', category: 'engagement', hint: 'At least N calls, texts or emails in a time window' },
  { kind: 'has_records', label: 'Has records on file', category: 'engagement', hint: 'Medical records or summaries uploaded or faxed in' },
];

export const kindMeta = (kind: string): KindMeta | undefined => KINDS.find((k) => k.kind === kind);

/** Fresh condition with sensible defaults for a just-picked kind. */
export function defaultConditionFor(kind: string): SearchCondition {
  switch (kind) {
    case 'stage': return { kind, statuses: [] };
    case 'stage_ever': return { kind, statuses: [] };
    case 'study': return { kind, slugs: [], scope: 'current' };
    case 'owner': return { kind, user_ids: [], include_unassigned: false };
    case 'incoming_eligibility': return { kind, values: [] };
    case 'milestone': return { kind, keys: [] };
    case 'lead_source': return { kind, sources: [] };
    case 'utm': return { kind, field: 'campaign', values: [] };
    case 'disposition': return { kind, to_statuses: [] };
    case 'tag': return { kind, tags: [] };
    case 'interest': return { kind, lists: [] };
    case 'created': return { kind, from: '', to: '' };
    case 'activity': return { kind, field: 'any', op: 'not_within', days: 30 };
    case 'contactable': return { kind, channel: 'sms' };
    case 'snoozed': return { kind, state: 'snoozed' };
    case 'archived': return { kind, state: 'archived' };
    case 'condition': return { kind, tag: '', name: '', statuses: ['active'] };
    case 'medication': return { kind, tag: '', name: '', statuses: ['active'] };
    case 'lab': return { kind, test_name: '', op: 'gte', value: '' };
    case 'vital': return { kind, preset: 'bmi', op: 'gte', value: '' };
    case 'demographic': return { kind, key: 'age', op: 'between', value: '', value2: '' };
    case 'form_submitted': return { kind, form_id: '', status: 'completed' };
    case 'answer': return { kind, question: '', op: 'eq', value: '' };
    case 'answered': return { kind, question: '' };
    case 'study_eligibility': return { kind, study_id: '', verdicts: [] };
    case 'ai_review': return { kind, results: [], latest_only: true };
    case 'dq': return { kind, categories: [] };
    case 'communication': return { kind, channels: ['call'], direction: 'any', within_days: 30, min_count: 1 };
    case 'has_records': return { kind, doc_types: ['medical_record'] };
    default: return { kind };
  }
}

/** Operators as words (plan §A4) — the editors' dropdowns and the sentences share them. */
export const OP_LABELS: Record<string, string> = {
  eq: 'is',
  neq: 'is not',
  gte: 'is at least',
  gt: 'is more than',
  lte: 'is at most',
  lt: 'is less than',
  between: 'is between',
  contains: 'contains',
  includes: 'includes',
  not_includes: "doesn't include",
  answered: 'answered (any answer)',
};

export const NUMERIC_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'];

export const VITAL_PRESET_OPTIONS = [
  { value: 'bmi', label: 'BMI' },
  { value: 'weight', label: 'Weight' },
  { value: 'height', label: 'Height' },
  { value: 'bp_systolic', label: 'Blood pressure (top number)' },
  { value: 'bp_diastolic', label: 'Blood pressure (bottom number)' },
  { value: 'heart_rate', label: 'Heart rate' },
];

/**
 * One-line sentence per server warning code (engine.js / catalog.js lint). The step card shows the
 * first one inline; the drill-in lists them all. `low_coverage` reads better with the percentage —
 * use `describeWarning`, which fills it in. Written so the advice is never wrong for the case it
 * fires in: a pinned form is the likeliest cause of low coverage, and "keep the people with no
 * answer" would then keep everyone who never saw that form.
 */
export const WARNING_LABELS: Record<string, string> = {
  low_coverage: 'Most of these people have nothing on file for this.',
  no_matches: 'Nobody matches this step.',
  matched_all: 'Everyone reaching this step matches it — check it is set up the way you meant.',
  milestone_truncated: 'There were too many people to check every milestone, so this count may be low.',
  unknown_kind: 'This step uses an older kind of step that no longer exists, so it matches nobody.',
  mixed_units: 'This lab is recorded in more than one unit. Values are compared as entered, never converted.',
  numeric_op_on_text: "This compares numbers, but this question's answers aren't numbers — it will match nobody.",
  contains_on_choice: "'contains' is unreliable on a pick-from-a-list question — use 'is' and pick the option instead.",
  form_pin_no_coverage: "Nobody answered this question on this form. They did answer it on another form — untick 'Only people who answered on this form' to include those.",
  drop_unknown_heavy: 'Dropping the people with no answer removes more than half of everyone reaching this step.',
};

/** A warning code as a plain sentence. Unknown codes never leak as raw text. */
export function describeWarning(code: string, opts: { coveragePct?: number | null; question?: boolean } = {}): string {
  if (code === 'low_coverage' && typeof opts.coveragePct === 'number') {
    return opts.question
      ? `Only ${opts.coveragePct}% of these people answered this question — the rest have no answer on file.`
      : `Only ${opts.coveragePct}% of these people have this on file — the rest have nothing on file for it.`;
  }
  return WARNING_LABELS[code] || 'Something about this step may need a second look.';
}

/** The first warning as a sentence + how many more there are (the step card's inline line). */
export function warningSummary(
  stats: Pick<SearchStepStats, 'warnings' | 'coverage_pct'> | null | undefined,
  question = false,
): { first: string; more: number; all: string[] } | null {
  const codes = stats?.warnings || [];
  if (!codes.length) return null;
  const all = codes.map((w) => describeWarning(w, { coveragePct: stats?.coverage_pct, question }));
  return { first: all[0], more: all.length - 1, all };
}

/** True when every condition in the step is about a form question — the "no answer" wording fits. */
export function isQuestionStep(step: Pick<SearchStepDef, 'conditions'> | null | undefined): boolean {
  const conds = step?.conditions || [];
  return conds.length > 0 && conds.every((c) => c.kind === 'answer' || c.kind === 'answered');
}

/**
 * The words for the people a step has no data on. Question steps use the plan's "no answer"
 * wording; other steps (a lab, a vital, a diagnosis) say "nothing on file", because "15% answered"
 * is nonsense for a BMI step.
 */
export function unknownWords(question: boolean): { count: string; people: string; tab: string; have: string } {
  return question
    ? { count: 'no answer', people: 'People with no answer', tab: 'No answer', have: 'answered' }
    : { count: 'nothing on file', people: 'People with nothing on file', tab: 'Nothing on file', have: 'have it on file' };
}

/**
 * The step's "dropped" count: people it ruled out who DID have data — the same set the drill-in's
 * Dropped tab pages (the server's excluded_with_data). The card and the drill-in both call this so
 * the two numbers can never disagree.
 *
 * `excluded` holds the no-data people too whenever the step drops them, so they're subtracted
 * exactly then. With the step definition we read its setting; without it (the run page's drill-in
 * only has the stats) the arithmetic tells: a step that passes its no-data people keeps
 * matched + no_data (include) or everyone but the matched (exclude).
 */
export function droppedWithData(
  stats: Pick<SearchStepStats, 'mode' | 'entering' | 'matched' | 'no_data' | 'excluded' | 'passing'>,
  step?: Pick<SearchStepDef, 'mode' | 'keep_unknown' | 'drop_unknown'> | null,
): number {
  let noDataPassed: boolean;
  if (step) {
    noDataPassed = step.mode === 'include' ? Boolean(step.keep_unknown) : !step.drop_unknown;
  } else if (stats.mode === 'exclude') {
    noDataPassed = stats.passing === stats.entering - stats.matched;
  } else {
    noDataPassed = stats.no_data > 0 && stats.passing === stats.matched + stats.no_data;
  }
  return Math.max(0, stats.excluded - (noDataPassed ? 0 : stats.no_data));
}

/** The forward spine in ordinal order (stage_ever's min_ordinal picker). */
export const SPINE_STAGES: { ordinal: number; value: string }[] = [
  { ordinal: 1, value: 'new' },
  { ordinal: 2, value: 'contacting' },
  { ordinal: 3, value: 'qualified_pending_records' },
  { ordinal: 4, value: 'ready_to_schedule' },
  { ordinal: 5, value: 'scheduled' },
  { ordinal: 6, value: 'screened' },
  { ordinal: 7, value: 'randomized' },
];

export const DISPOSITION_STATUS_OPTIONS = [
  { value: 'not_interested', label: 'Not interested' },
  { value: 'not_eligible', label: 'Not eligible' },
  { value: 'screen_failed', label: 'Screen failed' },
  { value: 'unreachable', label: 'Unreachable' },
];

/** Every close-out reason the CRM knows (dispositions + DQ categories), deduped, for labels. */
export const DISPOSITION_REASON_OPTIONS: { value: string; label: string }[] = (() => {
  const seen = new Map<string, string>();
  for (const list of Object.values(DISPOSITION_REASONS)) {
    for (const r of list) if (!seen.has(r.value)) seen.set(r.value, r.label);
  }
  for (const c of DQ_CATEGORIES) if (!seen.has(c.value)) seen.set(c.value, c.label);
  return [...seen.entries()].map(([value, label]) => ({ value, label }));
})();

/**
 * The DQ categories, straight from the shared vocabulary (lib.ts DQ_CATEGORIES — the close-out
 * modal's not_eligible reasons and the backend whitelist). A hand-kept copy here once left out
 * 'vitals', so "Closed out as Not eligible — Vitals" (BMI, blood pressure) couldn't be built.
 */
export const DQ_CATEGORY_OPTIONS: { value: string; label: string }[] =
  DQ_CATEGORIES.map((c) => ({ value: c.value, label: c.label }));

export const STUDY_ELIGIBILITY_VERDICT_OPTIONS = [
  { value: 'eligible', label: 'Eligible' },
  { value: 'screening', label: 'Prescreening' },
  { value: 'not_eligible', label: 'Not eligible' },
  { value: 'withdrew', label: 'Withdrew' },
];

export const AI_REVIEW_RESULT_OPTIONS = [
  { value: 'eligible', label: 'Eligible' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'review_required', label: 'Review required' },
  { value: 'incomplete', label: 'Incomplete' },
];

/** The editor's ad-detail dropdown (the utm_* field names stay out of the UI). */
export const UTM_FIELD_OPTIONS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'source', label: 'Source' },
  { value: 'medium', label: 'Medium' },
  { value: 'content', label: 'Ad content' },
  { value: 'term', label: 'Search term' },
];

const UTM_FIELD_WORDS: Record<string, string> = {
  campaign: 'Ad campaign', source: 'Ad source', medium: 'Ad medium', content: 'Ad content', term: 'Ad search term',
};

/** The lead-source codes that arrive on leads, in words. Unknown codes are title-cased. */
export const LEAD_SOURCE_LABELS: Record<string, string> = {
  fb: 'Facebook', facebook: 'Facebook', ig: 'Instagram', instagram: 'Instagram', an: 'Audience Network',
  msg: 'Messenger', manual: 'Added by hand', import: 'Imported', web: 'Our website', website: 'Our website',
};

export const COMM_CHANNEL_OPTIONS = [
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'Texts' },
  { value: 'email', label: 'Emails' },
];

export interface DescribeCtx {
  studies?: StudyOption[];
  team?: TeamMember[];
  forms?: CrmForm[];
  /** Question words by key (GET /search/question-labels) — heals definitions saved without labels. */
  questionLabels?: Record<string, QuestionLabelInfo>;
}

/** "A", "A or B", "A, B or C", "A, B, C or 2 more". */
function joinList(vals: string[], word: 'or' | 'and' = 'or', max = 3): string {
  if (!vals.length) return '…';
  if (vals.length === 1) return vals[0];
  if (vals.length > max) return `${vals.slice(0, max).join(', ')} ${word} ${vals.length - max} more`;
  return `${vals.slice(0, -1).join(', ')} ${word} ${vals[vals.length - 1]}`;
}

const list = (vals: string[] | undefined, map: (v: string) => string, word: 'or' | 'and' = 'or'): string =>
  joinList((vals || []).map(map), word);

/** "2026-01-05" → "Jan 5, 2026" (no timezone drift: it's a calendar date, not an instant). */
function fmtDay(v: string | null | undefined): string | null {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (!m) return String(v);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "condition.type_2_diabetes" → "Type 2 diabetes" (a field tag with no label on the condition). */
export function tagWords(tag: string | null | undefined): string {
  const raw = String(tag || '').trim();
  if (!raw) return '';
  const last = raw.includes('.') ? raw.slice(raw.lastIndexOf('.') + 1) : raw;
  const words = last.replace(/[-_]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : raw;
}

// ---------------------------------------------------------------------------------------------
// Question labels (plan §A3) — what a question key means, fetched once per key per session.
// ---------------------------------------------------------------------------------------------

const QUESTION_LABELS_MAX = 50;
/** key → info, or null when the server knows no form that asks it (a negative cache). */
const questionLabelCache = new Map<string, QuestionLabelInfo | null>();
/** The api predates the endpoint (404) — stop asking for the rest of the session. */
let questionLabelsUnavailable = false;
/**
 * key → the lookup already asking for it. The builder, the funnel rail and every answer editor
 * ask for the same keys at the same moment when a search opens; without this each would miss the
 * cache (it fills only when a response lands) and send its own identical request, and on a cold
 * server each of those rebuilds the whole question index.
 */
const pendingLabelKeys = new Map<string, Promise<void>>();

/** The cached words for one key (null = unknown or not fetched yet). */
export function cachedQuestionLabel(key: string | null | undefined): QuestionLabelInfo | null {
  return key ? questionLabelCache.get(key) || null : null;
}

/** Seed the session cache (tests, and any page that already holds the words). */
export function primeQuestionLabels(labels: Record<string, QuestionLabelInfo | null>): void {
  for (const [k, v] of Object.entries(labels)) questionLabelCache.set(k, v);
}

/** Forget everything learned this session (tests). */
export function resetQuestionLabelCache(): void {
  questionLabelCache.clear();
  pendingLabelKeys.clear();
  questionLabelsUnavailable = false;
}

function normalizeLabelInfo(raw: any): QuestionLabelInfo | null {
  if (!raw || typeof raw !== 'object' || typeof raw.label !== 'string' || !raw.label.trim()) return null;
  return {
    label: raw.label,
    type: typeof raw.type === 'string' ? raw.type : null,
    options: Array.isArray(raw.options)
      ? raw.options.filter((o: any) => o && o.value !== undefined).map((o: any) => ({ value: String(o.value), label: String(o.label ?? o.value) }))
      : [],
    forms: Array.isArray(raw.forms)
      ? raw.forms.filter((f: any) => f && f.id).map((f: any) => ({ id: String(f.id), title: String(f.title || ''), surface: f.surface ?? null }))
      : [],
  };
}

/** Every answer / answered question key in a definition, deduped (the question-labels lookup). */
export function questionKeysOf(def: { steps?: SearchStepDef[] } | null | undefined): string[] {
  const keys = new Set<string>();
  for (const s of def?.steps || []) {
    for (const c of s.conditions || []) {
      if ((c.kind === 'answer' || c.kind === 'answered') && typeof c.question === 'string') {
        const k = c.question.trim();
        if (k && k.length <= 200 && !k.includes(',')) keys.add(k);
      }
    }
  }
  return [...keys];
}

/**
 * Fetch the words for `keys` (≤50 per call, chunked), caching per key for the session. Resolves to
 * the known entries only. Never throws: a failed call just leaves the key unlabelled (retried on a
 * later call); a 404 means this api doesn't serve the endpoint yet, so the session stops asking.
 * A key another caller is already asking for is not asked for again — this waits for that answer.
 */
export async function fetchQuestionLabels(keys: string[]): Promise<Record<string, QuestionLabelInfo>> {
  const unique = [...new Set(keys.filter((k) => k && !k.includes(',')))];
  const already = new Set<Promise<void>>();
  for (const k of unique) {
    const p = pendingLabelKeys.get(k);
    if (p) already.add(p);
  }
  const want = unique.filter((k) => !questionLabelCache.has(k) && !pendingLabelKeys.has(k));
  if (want.length && !questionLabelsUnavailable) {
    const own = (async () => {
      for (let i = 0; i < want.length && !questionLabelsUnavailable; i += QUESTION_LABELS_MAX) {
        const chunk = want.slice(i, i + QUESTION_LABELS_MAX);
        try {
          const res = await api.get('/api/crm/search/question-labels', { params: { keys: chunk.join(',') } });
          const labels = res?.data?.labels || {};
          for (const k of chunk) questionLabelCache.set(k, normalizeLabelInfo(labels[k]));
        } catch (e: any) {
          if (e?.response?.status === 404) questionLabelsUnavailable = true;
          break;
        }
      }
    })();
    // Registered before the first await anywhere, so a caller in the same tick shares it.
    for (const k of want) pendingLabelKeys.set(k, own);
    already.add(own.finally(() => {
      for (const k of want) if (pendingLabelKeys.get(k) === own) pendingLabelKeys.delete(k);
    }));
  }
  await Promise.all(already);
  const out: Record<string, QuestionLabelInfo> = {};
  for (const k of unique) {
    const v = questionLabelCache.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function cachedLabelsFor(keys: string[]): Record<string, QuestionLabelInfo> {
  const out: Record<string, QuestionLabelInfo> = {};
  for (const k of keys) {
    const v = questionLabelCache.get(k);
    if (v) out[k] = v;
  }
  return out;
}

/**
 * The words for `keys`, loaded after a short pause (so a key being typed isn't looked up per
 * keystroke). Returns what's known so far — cached keys immediately, the rest when they arrive.
 * Used by the builder, the funnel rail and the answer editor; SearchRun can call it too.
 */
export function useQuestionLabels(keys: string[]): Record<string, QuestionLabelInfo> {
  const sig = [...new Set(keys.filter(Boolean))].sort().join('\n');
  const [labels, setLabels] = useState<Record<string, QuestionLabelInfo>>(() => cachedLabelsFor(sig ? sig.split('\n') : []));
  useEffect(() => {
    const wanted = sig ? sig.split('\n') : [];
    setLabels(cachedLabelsFor(wanted));
    if (!wanted.length || wanted.every((k) => questionLabelCache.has(k))) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      fetchQuestionLabels(wanted).then((m) => { if (alive) setLabels(m); }).catch(() => {});
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [sig]);
  return labels;
}

/**
 * Fill missing display fields on answer / answered conditions from question labels: the question's
 * words, the picked option's words, and the pinned form's title + surface. Never touches `type`,
 * `op`, `value` or `form_id` — those change what the search matches. `changed` = anything filled.
 */
export function applyQuestionLabels(
  def: SearchDefinition,
  labels: Record<string, QuestionLabelInfo>,
): { def: SearchDefinition; changed: boolean } {
  let changed = false;
  const steps = def.steps.map((s) => {
    let stepChanged = false;
    const conditions = s.conditions.map((c) => {
      if ((c.kind !== 'answer' && c.kind !== 'answered') || !c.question) return c;
      const info = labels[c.question];
      if (!info) return c;
      const next: SearchCondition = { ...c };
      let touched = false;
      if (!c.label && info.label) { next.label = info.label; touched = true; }
      if (c.kind === 'answer' && !c.valueLabel && c.value !== undefined && c.value !== '') {
        const opt = info.options.find((o) => o.value === String(c.value));
        if (opt && opt.label !== opt.value) { next.valueLabel = opt.label; touched = true; }
      }
      if (c.form_id && !c.picker_form_title) {
        const f = info.forms.find((x) => x.id === c.form_id);
        if (f && f.title) {
          next.picker_form_id = c.form_id;
          next.picker_form_title = f.title;
          if (f.surface) next.picker_form_surface = f.surface;
          touched = true;
        }
      }
      if (!touched) return c;
      stepChanged = true;
      return next;
    });
    if (!stepChanged) return s;
    changed = true;
    return { ...s, conditions };
  });
  return { def: changed ? { ...def, steps } : def, changed };
}

// ---------------------------------------------------------------------------------------------
// Form picker (plan §A5) — Landing pages vs Texted forms, each by volume.
// ---------------------------------------------------------------------------------------------

/** How much a form has been used: landing leads for a landing page, completions for a texted form. */
export function formVolume(f: CrmForm): number {
  return formSurface(f.surface) === 'landing'
    ? Number(f.landing_leads ?? f.counts?.completed ?? 0) || 0
    : Number(f.counts?.completed ?? 0) || 0;
}

/** Split forms into the picker's two groups, each sorted by volume (then title). */
export function groupFormsForPicker(forms: CrmForm[]): { landing: CrmForm[]; texted: CrmForm[] } {
  const byVolume = (a: CrmForm, b: CrmForm) => formVolume(b) - formVolume(a) || a.title.localeCompare(b.title);
  return {
    landing: forms.filter((f) => formSurface(f.surface) === 'landing').sort(byVolume),
    texted: forms.filter((f) => formSurface(f.surface) !== 'landing').sort(byVolume),
  };
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** "Diabetic Weight Loss · Metabolic Health Study · 1,248 leads" / "GZVA Questionnaire · 32 completed". */
export function formOptionLabel(f: CrmForm): string {
  const parts = [f.title || 'Untitled form'];
  if (formSurface(f.surface) === 'landing') {
    if (f.study_title) parts.push(f.study_title);
    if (typeof f.landing_leads === 'number') parts.push(plural(f.landing_leads, 'lead'));
  } else if (f.counts) {
    parts.push(`${f.counts.completed.toLocaleString('en-US')} completed`);
  }
  if (f.status === 'archived') parts.push('archived');
  return parts.join(' · ');
}

/** "the Diabetic Weight Loss landing page" / "the GZVA texted form" — never doubles the noun. */
export function formPhrase(title: string | null | undefined, surface: string | null | undefined): string {
  const t = String(title || '').trim();
  const kind = surface ? formSurface(surface) : null;
  if (!t) return kind === 'landing' ? 'a landing page' : kind === 'questionnaire' ? 'a texted form' : 'a form';
  if (kind === 'landing') return /landing( page)?$/i.test(t) ? `the ${t}` : `the ${t} landing page`;
  if (kind === 'questionnaire') return /(form|questionnaire|survey)$/i.test(t) ? `the texted ${t}` : `the ${t} texted form`;
  return /(form|page|questionnaire)$/i.test(t) ? `the ${t}` : `the ${t} form`;
}

/** Title + surface of a form by id: the forms list, else the condition's picker fields, else labels. */
function formInfoFor(id: string, c: SearchCondition, ctx: DescribeCtx): { title: string | null; surface: string | null } {
  const f = (ctx.forms || []).find((x) => x.id === id);
  if (f) return { title: f.title, surface: f.surface || 'questionnaire' };
  if (c.picker_form_id === id && c.picker_form_title) return { title: c.picker_form_title, surface: c.picker_form_surface || null };
  const info = c.question ? (ctx.questionLabels?.[c.question] || cachedQuestionLabel(c.question)) : null;
  const lf = info?.forms.find((x) => x.id === id);
  if (lf) return { title: lf.title || null, surface: lf.surface };
  return { title: null, surface: null };
}

// ---------------------------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------------------------

const YES_NO = /^(yes|no|true|false)$/i;
const yesNoWord = (v: string) => (/^(yes|true)$/i.test(v) ? 'Yes' : 'No');
const NUM_TYPES = new Set(['number', 'scale', 'calculation', 'height']);

/** The answer / answered sentence (plan §A4). */
function describeAnswer(c: SearchCondition, ctx: DescribeCtx): string {
  const key = (c.question || '').trim();
  const info = key ? (ctx.questionLabels?.[key] || cachedQuestionLabel(key)) : null;
  const words = c.label || info?.label || null;
  if (!key) return c.kind === 'answered' ? 'Answered a question (pick one)' : 'An answer (pick a question)';
  const q = words ? `"${words}"` : `question ID ${key}`;

  const suffix = c.form_id
    ? (() => {
      const fi = formInfoFor(c.form_id, c, ctx);
      return ` on ${formPhrase(fi.title, fi.surface)}`;
    })()
    : ' on any form that asks it';

  if (c.kind === 'answered' || c.op === 'answered') return `Answered ${q}${suffix}`;

  const type = c.type || info?.type || undefined;
  const raw = c.value === undefined || c.value === null ? '' : String(c.value);
  const optLabel = info?.options.find((o) => o.value === raw)?.label;
  const shown = c.valueLabel || optLabel || raw || '…';
  const op = c.op || 'eq';

  const yesNo = type === 'yes_no' || (!type && YES_NO.test(raw) && (op === 'eq' || op === 'neq'));
  if (yesNo && (op === 'eq' || op === 'neq')) {
    const v = raw ? yesNoWord(raw) : '…';
    return `${op === 'eq' ? 'Said' : "Didn't say"} "${v}" to ${q}${suffix}`;
  }
  const numeric = ['gt', 'gte', 'lt', 'lte', 'between'].includes(op) || (type ? NUM_TYPES.has(type) : false);
  if (numeric) {
    if (op === 'between') return `${q} is between ${raw || '…'} and ${c.value2 ?? '…'}${suffix}`;
    return `${q} ${OP_LABELS[op] || 'is'} ${raw || '…'}${suffix}`;
  }
  if (op === 'includes') return `Picked "${shown}" for ${q}${suffix}`;
  if (op === 'not_includes') return `Didn't pick "${shown}" for ${q}${suffix}`;
  if (op === 'contains') return `${q} contains "${shown}"${suffix}`;
  if (op === 'neq') return `Didn't answer "${shown}" to ${q}${suffix}`;
  return `Answered "${shown}" to ${q}${suffix}`;
}

/** One-line plain-English sentence for a condition (step rows, funnel labels, chips). */
export function describeCondition(c: SearchCondition, ctx: DescribeCtx = {}): string {
  const stageLabel = (v: string) => LIFECYCLES.find((l) => l.value === v)?.label || prettySlug(v);
  const studyLabel = (slug: string) => {
    const s = (ctx.studies || []).find((x) => x.slug === slug);
    return s ? studyInternalLabel(s) : prettySlug(slug);
  };
  const studyByIdLabel = (id: string) => {
    const s = (ctx.studies || []).find((x) => x.id === id);
    return s ? studyInternalLabel(s) : 'a study';
  };
  const ownerLabel = (id: string) => (ctx.team || []).find((m) => m.id === id)?.name || 'a former team member';
  const numPhrase = () => (c.op === 'between'
    ? `is between ${c.value ?? '…'} and ${c.value2 ?? '…'}`
    : `${OP_LABELS[c.op || ''] || 'is'} ${c.value === '' || c.value === undefined ? '…' : c.value}`);
  const within = c.within_days ? ` (in the last ${c.within_days} days)` : '';
  const verified = c.require_verified ? ' (verified)' : '';

  switch (c.kind) {
    case 'stage': return `Stage is ${list(c.statuses, stageLabel)}`;
    case 'stage_ever': {
      if (c.min_ordinal !== undefined && c.min_ordinal !== null && String(c.min_ordinal) !== '') {
        const spine = SPINE_STAGES.find((s) => s.ordinal === Number(c.min_ordinal));
        return `Ever reached ${spine ? stageLabel(spine.value) : 'a later stage'} or further`;
      }
      return `Was ever at ${list(c.statuses, stageLabel)}`;
    }
    case 'study':
      return c.scope === 'ever'
        ? `Ever applied to ${list(c.slugs, studyLabel)}`
        : `Being considered for ${list(c.slugs, studyLabel)}`;
    case 'no_study': return 'Not being considered for any study';
    case 'owner': {
      const parts = (c.user_ids || []).map(ownerLabel);
      if (c.include_unassigned) {
        if (!parts.length) return 'Has no owner';
        parts.push('nobody');
      }
      return `Owned by ${joinList(parts)}`;
    }
    case 'incoming_eligibility':
      return `Landing-page eligibility is ${list(c.values, (v) => INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === v)?.label || prettySlug(v))}`;
    case 'milestone':
      return `Milestone is ${list(c.keys, (v) => (MILESTONE_LABELS as Record<string, string>)[v] || prettySlug(v))}`;
    case 'lead_source':
      return `Came from ${list(c.sources, (v) => LEAD_SOURCE_LABELS[v.toLowerCase()] || prettySlug(v))}`;
    case 'utm':
      return `${UTM_FIELD_WORDS[c.field || ''] || 'Ad detail'} is ${list(c.values, (v) => v)}`;
    case 'disposition': {
      const what = (c.to_statuses || []).length
        ? list(c.to_statuses, (v) => DISPOSITION_STATUS_OPTIONS.find((o) => o.value === v)?.label || prettySlug(v))
        : 'anything';
      const why = (c.reason_codes || []).length
        ? ` — ${list(c.reason_codes, (v) => DISPOSITION_REASON_OPTIONS.find((o) => o.value === v)?.label || prettySlug(v))}`
        : '';
      const on = (c.study_slugs || []).length ? ` on ${list(c.study_slugs, studyLabel)}` : '';
      const from = fmtDay(c.from as string | null);
      const to = fmtDay(c.to as string | null);
      const when = from && to ? ` between ${from} and ${to}` : from ? ` since ${from}` : to ? ` before ${to}` : '';
      return `Closed out as ${what}${why}${on}${when}`;
    }
    case 'tag':
      // Export tags are minted as "search:<slug>" — the prefix is plumbing, not the tag's name.
      return `Tagged ${list(c.tags, (v) => `"${v.replace(/^search:/, '')}"`)}`;
    case 'interest': return `On the ${list(c.lists, prettySlug)} future-study list`;
    case 'created': {
      const from = fmtDay(c.from as string | null);
      const to = fmtDay(c.to as string | null);
      if (from && to) return `Came in between ${from} and ${to}`;
      if (from) return `Came in on or after ${from}`;
      if (to) return `Came in on or before ${to}`;
      return 'Came in (pick dates)';
    }
    case 'activity': {
      const days = c.days ?? '…';
      const yes = c.op === 'within';
      if (c.field === 'inbound') return yes ? `Heard from them in the last ${days} days` : `Haven't heard from them in ${days} days`;
      if (c.field === 'outbound') return yes ? `We contacted them in the last ${days} days` : `We haven't contacted them in ${days} days`;
      return yes ? `Any activity in the last ${days} days` : `No activity in the last ${days} days`;
    }
    case 'contactable': return c.channel === 'email' ? 'Can be emailed' : 'Can be texted';
    case 'snoozed': return c.state === 'not_snoozed' ? 'Not snoozed' : 'Snoozed right now';
    case 'archived': return c.state === 'active' ? 'Not archived' : 'Archived';
    case 'condition':
    case 'medication': {
      const what = c.tag_label || tagWords(c.tag) || (c.name || '').trim() || '…';
      // The default is "active only" — say the status only when the step asks for something else.
      const statuses = c.statuses || [];
      const statusWords: Record<string, string> = { active: 'active', resolved: 'resolved', stopped: 'stopped', unknown: 'status unknown' };
      const status = statuses.length && !(statuses.length === 1 && statuses[0] === 'active')
        ? ` (${joinList(statuses.map((s) => statusWords[s] || prettySlug(s).toLowerCase()))})`
        : '';
      return `${c.kind === 'condition' ? 'Has' : 'Takes'} "${what}"${status}${verified}`;
    }
    case 'lab': {
      const what = c.tag_label || tagWords(c.tag) || (c.test_name || '').trim() || '…';
      return c.op ? `${what} ${numPhrase()}${within}${verified}` : `Has a ${what} result${within}${verified}`;
    }
    case 'vital': {
      const label = VITAL_PRESET_OPTIONS.find((p) => p.value === c.preset)?.label || 'A vital';
      return c.op ? `${label} ${numPhrase()}${within}${verified}` : `Has a ${label} on file${within}${verified}`;
    }
    case 'demographic':
      return c.key === 'sex'
        ? `Sex is ${list(c.values, (v) => prettySlug(v))}`
        : `Age ${numPhrase()}`;
    case 'study_eligibility':
      return `Prescreen for ${c.study_id ? studyByIdLabel(c.study_id) : '…'} is ${list(c.verdicts, (v) => STUDY_ELIGIBILITY_VERDICT_OPTIONS.find((o) => o.value === v)?.label || prettySlug(v))}`;
    case 'ai_review':
      return `AI records review${c.study_id ? ` for ${studyByIdLabel(c.study_id)}` : ''} came back ${list(c.results, (v) => AI_REVIEW_RESULT_OPTIONS.find((o) => o.value === v)?.label || prettySlug(v))}`;
    case 'dq': {
      const why = (c.categories || []).length
        ? ` for ${list(c.categories, (v) => (DQ_CATEGORY_OPTIONS.find((o) => o.value === v)?.label || prettySlug(v)).toLowerCase())}`
        : '';
      const on = (c.study_slugs || []).length ? ` on ${list(c.study_slugs, studyLabel)}` : '';
      return `Screened out${why}${on}`;
    }
    case 'communication': {
      const n = Number(c.min_count ?? 1) || 1;
      const nouns: Record<string, [string, string]> = { call: ['call', 'calls'], sms: ['text', 'texts'], email: ['email', 'emails'] };
      const ch = list(c.channels, (v) => (nouns[v] ? nouns[v][n === 1 ? 0 : 1] : v));
      const dir = c.direction === 'inbound' ? 'incoming ' : c.direction === 'outbound' ? 'outgoing ' : '';
      return `At least ${n} ${dir}${ch} in the last ${c.within_days ?? '…'} days`;
    }
    case 'form_submitted': {
      if (!c.form_id) return 'Filled in a form (pick one)';
      const fi = formInfoFor(c.form_id, c, ctx);
      return `${c.status === 'any' ? 'Started or filled in' : 'Filled in'} ${formPhrase(fi.title, fi.surface)}`;
    }
    case 'answer':
    case 'answered':
      return describeAnswer(c, ctx);
    case 'has_records': {
      const words: Record<string, string> = { medical_record: 'medical records', prescreening_summary: 'prescreening summaries' };
      return `Has ${list(c.doc_types || ['medical_record'], (v) => words[v] || prettySlug(v).toLowerCase())} on file`;
    }
    default:
      // A retired kind (the server marks it degraded) or one this build doesn't know — never the
      // raw kind code.
      return 'An older kind of step that no longer exists';
  }
}

/** Step summary for the funnel rail: its name, or its first condition + how many more. */
export function describeStep(step: SearchStepDef, ctx: DescribeCtx = {}): string {
  if (step.label) return step.label;
  if (!step.conditions.length) return 'Empty step';
  const first = describeCondition(step.conditions[0], ctx);
  const more = step.conditions.length - 1;
  const joiner = step.match === 'all' ? 'and' : 'or';
  return `${step.mode === 'exclude' ? 'Leave out: ' : ''}${first}${more > 0 ? ` ${joiner} ${more} more` : ''}`;
}

export const newStepId = (): string => `s_${Math.random().toString(36).slice(2, 10)}`;

export const emptyDefinition = (): SearchDefinition => ({
  version: 1,
  base: { exclude_archived: true, exclude_test: true, created_from: null, created_to: null },
  steps: [],
});

/** A condition is complete enough to evaluate (mirror of catalog validators —
 *  approximate on purpose; the server 400s with specifics on real errors). */
export function isConditionReady(c: SearchCondition): boolean {
  switch (c.kind) {
    case 'stage': return (c.statuses || []).length > 0;
    case 'stage_ever':
      return (c.statuses || []).length > 0
        || (c.min_ordinal !== undefined && c.min_ordinal !== null && String(c.min_ordinal) !== '');
    case 'study': return (c.slugs || []).length > 0;
    case 'utm': return Boolean(c.field) && (c.values || []).length > 0;
    case 'disposition': return true; // empty = "ever dispositioned" is valid
    case 'demographic': {
      if (c.key === 'sex') return (c.values || []).length > 0;
      if (!c.op || String(c.value ?? '').trim() === '' || !Number.isFinite(Number(c.value))) return false;
      if (c.op === 'between') return String(c.value2 ?? '').trim() !== '' && Number.isFinite(Number(c.value2));
      return true;
    }
    case 'study_eligibility': return Boolean(c.study_id) && (c.verdicts || []).length > 0;
    case 'ai_review': return (c.results || []).length > 0;
    case 'dq': return true; // empty = "ever disqualified" is valid
    case 'communication': return (c.channels || []).length > 0 && Number(c.within_days) >= 1;
    case 'owner': return (c.user_ids || []).length > 0 || c.include_unassigned === true;
    case 'incoming_eligibility': return (c.values || []).length > 0;
    case 'milestone': return (c.keys || []).length > 0;
    case 'lead_source': return (c.sources || []).length > 0;
    case 'tag': return (c.tags || []).length > 0;
    case 'interest': return (c.lists || []).length > 0;
    case 'created': return Boolean(c.from || c.to);
    case 'activity': return Number(c.days) >= 1;
    case 'condition':
    case 'medication': return Boolean((c.tag || '').trim() || (c.name || '').trim());
    case 'lab': {
      if (!(c.tag || '').trim() && !(c.test_name || '').trim()) return false;
      if (!c.op) return true;
      if (String(c.value ?? '').trim() === '' || !Number.isFinite(Number(c.value))) return false;
      if (c.op === 'between') return String(c.value2 ?? '').trim() !== '' && Number.isFinite(Number(c.value2));
      return true;
    }
    case 'vital': {
      if (!c.preset) return false;
      if (!c.op) return true;
      if (String(c.value ?? '').trim() === '' || !Number.isFinite(Number(c.value))) return false;
      if (c.op === 'between') return String(c.value2 ?? '').trim() !== '' && Number.isFinite(Number(c.value2));
      return true;
    }
    case 'form_submitted': return Boolean(c.form_id);
    case 'answer':
      return Boolean((c.question || '').trim()) && (c.op === 'answered' || String(c.value ?? '').trim() !== '');
    case 'answered': return Boolean((c.question || '').trim());
    default: return true; // no_study / contactable / snoozed / archived / has_records
  }
}

/**
 * The definition as sent to the API: incomplete conditions and emptied steps dropped, the
 * server-set `degraded` flag stripped. Display fields are KEPT (plan §A3) — the server stores the
 * posted definition verbatim, and its validators leave them out of the evaluated criterion, so the
 * stored search keeps its words without changing what it matches or its cache key.
 */
export function wireDefinition(def: SearchDefinition): SearchDefinition {
  return {
    version: 1,
    base: { ...def.base },
    steps: def.steps
      .map((s) => ({
        ...s,
        conditions: s.conditions
          .filter(isConditionReady)
          .map((c) => {
            const { degraded, ...rest } = c;
            return rest as SearchCondition;
          }),
      }))
      .filter((s) => s.conditions.length > 0),
  };
}

/** The answer / answered fields that only say what a step means in words — never what it matches. */
const DISPLAY_ONLY_FIELDS = ['label', 'valueLabel', 'picker_form_id', 'picker_form_title', 'picker_form_surface'] as const;

/**
 * What the live preview has to re-count for: the wire definition minus the display-only fields.
 * Filling in a question's missing words on open (applyQuestionLabels) changes the definition but
 * not this, so it doesn't send a second, identical preview evaluation.
 */
export function previewSignature(def: SearchDefinition): string {
  const wire = wireDefinition(def);
  return JSON.stringify({
    ...wire,
    steps: wire.steps.map((s) => ({
      ...s,
      conditions: s.conditions.map((c) => {
        if (c.kind !== 'answer' && c.kind !== 'answered') return c;
        const bare: Record<string, unknown> = { ...c };
        for (const f of DISPLAY_ONLY_FIELDS) delete bare[f];
        return bare;
      }),
    })),
  });
}

// ---------------------------------------------------------------------------------------------
// Run status + "Attach to a study" (ExportModal) words
// ---------------------------------------------------------------------------------------------

/** A search run's status in words (the library table and the recent-runs strip). */
export function runStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'queued': return 'Waiting to run';
    case 'running': return 'Running…';
    case 'failed': return "Didn't finish";
    case 'complete': return 'Done';
    default: return 'Unknown';
  }
}

/** "step 2 (Answer): pick Yes or No" → "Step 2 (Answer): pick Yes or No." */
function asSentence(s: string): string {
  const t = String(s || '').trim();
  if (!t) return '';
  const cap = t[0].toUpperCase() + t.slice(1);
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
}

/**
 * A search the server refused as written (a 400, or a run stored as `validation: …`): its sentence
 * plus what to do. Running it again fails the same way, so the advice is to edit it.
 */
export function searchNeedsEditText(reason: string): string {
  const s = asSentence(reason);
  return s ? `${s} Edit the search to fix it.` : 'Something in this search needs changing. Edit the search to fix it.';
}

const VALIDATION_PREFIX = 'validation: ';

/**
 * Why a run didn't finish, in words (plan §A6). A run's stored error is a developer string, so it
 * is never shown as-is: a budget overrun gets its own advice, everything else a generic line —
 * except a `validation: ` error (crmSearch/runs.js executeRun), whose sentence was written for the
 * recruiter ("Step 2 (Answer): pick Yes or No") and is the only way they learn what to change.
 */
export function runFailureText(error: string | null | undefined): string {
  const e = String(error || '');
  if (e.startsWith(VALIDATION_PREFIX)) return searchNeedsEditText(e.slice(VALIDATION_PREFIX.length));
  if (e.includes('budget_exceeded')) {
    return 'It has to look through more records than a single search is allowed to. Narrow who it starts with, or make the first step more specific, then run it again.';
  }
  return 'Something went wrong while it was running. Try running it again.';
}

/**
 * A 400 about one step says "Step N", counting the steps the server was SENT. The builder sends
 * wireDefinition(def), which leaves out unfinished steps, so N can name the wrong card. The 400
 * carries the step's id (`step_id`), so renumber it to that card's position. A message about no
 * particular step, or an id not on the page, is left as it is.
 */
export function renumberStepError(
  message: string,
  stepId: string | null | undefined,
  steps: { id: string }[] | null | undefined,
): string {
  const msg = String(message || '');
  if (!stepId || !steps) return msg;
  const at = steps.findIndex((s) => s.id === stepId);
  return at >= 0 ? msg.replace(/^Step \d+/, `Step ${at + 1}`) : msg;
}

/**
 * A step's number as its card shows it: its position in the full definition (unfinished steps
 * included), falling back to the stats order when the definition isn't known.
 */
export function stepNumber(stepId: string, stepDefs: { id: string }[] | null | undefined, fallbackIndex: number): number {
  const at = (stepDefs || []).findIndex((d) => d.id === stepId);
  return at >= 0 ? at + 1 : fallbackIndex + 1;
}

/** Why a person on the list won't get the campaign message (export skip codes, incl. BE-2's two). */
export const EXPORT_SKIP_LABELS: Record<string, string> = {
  opted_out: 'Opted out of texts or emails',
  no_channel: 'No phone number or email on file',
  no_consent_basis: 'Imported or added by hand, and never texted us',
  recently_contacted: 'Contacted recently',
  active_elsewhere: 'Being actively worked for another study',
  closed_lead: 'Closed lead (not interested, not eligible, unreachable, enrolled, or archived)',
  wrong_person: 'Wrong number on file',
  canceled: 'Cancelled before it was sent',
};

export const exportSkipLabel = (code: string | null | undefined): string =>
  (code && EXPORT_SKIP_LABELS[code]) || 'Skipped';

/**
 * The attach preflight's advisory warnings (exportAttach.preflightAttach) are written for
 * developers ("round_robin: …", "lead(s)"). Map the known shapes to sentences; anything else is
 * shown as the server wrote it, capitalised.
 */
export function exportWarningText(w: string): string {
  const s = String(w || '').trim();
  if (/^round_robin\b/i.test(s)) return 'This study has no active recruiters, so nobody will be given an owner.';
  const moved = /^overwrite will move (\d+) lead/i.exec(s);
  if (moved) {
    const n = Number(moved[1]);
    return `This will move ${plural(n, 'lead')} off the study they are being considered for now.`;
  }
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** The export route's 400s (exportCampaign.normalizeCampaignParams) as sentences; others pass through. */
export function exportParamErrorText(err: string): string {
  const s = String(err || '').trim();
  // The message (exportCampaign.normalizeCampaignMessage) — worded as campaignMessage.ts words them.
  if (/campaign message is (required|empty)|template_id is required/.test(s)) return 'Write the message first.';
  const tooLong = /^campaign message is longer than (\d+) characters/.exec(s);
  if (tooLong) return `The message is too long — keep it under ${Number(tooLong[1]).toLocaleString()} characters.`;
  if (/email campaigns need a subject line/.test(s)) return 'Add a subject line for the email.';
  const subjectLong = /^campaign subject is longer than (\d+) characters/.exec(s);
  if (subjectLong) return `The subject line is too long — keep it under ${subjectLong[1]} characters.`;
  const unknown = /^campaign message has unknown merge fields?: (.+)$/.exec(s);
  if (unknown) return `The message uses something we can't fill in for each person: ${unknown[1]}. Use the Variables list.`;
  const answers = /^campaign message can't use form answers: (.+)$/.exec(s);
  if (answers) return `Form answers can't be filled into a message sent to a list: ${answers[1]}.`;
  const template = /^template: (.+)$/.exec(s);
  if (template) return templateErrorText(template[1]);
  if (/start_at is in the past/.test(s)) return 'The start time has already passed — pick a later one.';
  if (/start_at must be within/.test(s)) return 'Start within the next 14 days.';
  if (/start_at is invalid/.test(s)) return 'Pick a start time.';
  if (/pace_per_hour/.test(s)) return 'Messages per hour must be between 1 and 600.';
  if (/recently_contacted_days/.test(s)) return '"Contacted recently" must be between 1 and 90 days.';
  if (/channel must be/.test(s)) return 'Pick text or email.';
  return s;
}

/** exportCampaign.validateCampaignTemplate's reasons, as sentences (unknown text passes through). */
export function templateErrorText(err: string | null | undefined): string {
  const s = String(err || '').trim();
  if (!s) return '';
  if (s === 'template not found') return 'That template no longer exists.';
  if (s === 'template is archived') return 'That template has been archived.';
  if (/^template is \w+-only/.test(s)) return 'That template is for a different kind of message.';
  if (/subject line/.test(s)) return 'Emails need a template with a subject line.';
  const unknown = /^template has unknown merge fields?: (.+)$/.exec(s);
  if (unknown) return `The template uses something we can't fill in for each person: ${unknown[1]}.`;
  return s[0].toUpperCase() + s.slice(1);
}

/**
 * The "move them to this study as new leads" summary (exportAttach move_new preflight), one
 * sentence per line; empty for the other modes. Says who moves, who is reopened, who stays on
 * another study's list too, and who is left exactly as they are, and why.
 */
export function attachMoveLines(pf: ExportAttachPreflight): string[] {
  if (pf.set_considering !== 'move_new') return [];
  const people = (n: number) => plural(n, 'person', 'people');
  const lines = [
    `Will open ${people(pf.to_move ?? 0)} as new leads on this study${pf.lead_source ? ` (source: ${pf.lead_source})` : ''}`,
  ];
  if (pf.to_move_back) lines.push(`${people(pf.to_move_back)} already on this study will be moved back to it where they left off`);
  if (pf.to_reopen) lines.push(`${plural(pf.to_reopen, 'closed or archived lead')} will be reopened`);
  if (pf.from_other_study) {
    lines.push(`${people(pf.from_other_study)} being considered for another study will stay on that study's list with the status they had`);
  }
  if (pf.auto_owner) lines.push(`${people(pf.auto_owner)} with no owner will get one of this study's recruiters`);
  const k = pf.kept;
  const kept = k ? [
    k.committed ? `${k.committed.toLocaleString('en-US')} in or headed into a study` : null,
    k.already_on_study ? `${k.already_on_study.toLocaleString('en-US')} already on this study` : null,
    k.in_agent_conversation ? `${k.in_agent_conversation.toLocaleString('en-US')} talking with the texting agent` : null,
    k.on_outreach_list ? `${k.on_outreach_list.toLocaleString('en-US')} on an outreach list` : null,
  ].filter(Boolean) : [];
  if (kept.length) lines.push(`Left as they are: ${kept.join(', ')}`);
  return lines;
}

/** What an 'attached' receipt changed ("tag+considering+owner" → "tagged, study set, owner set"). */
export function attachActionsText(detail: string | null | undefined): string {
  if (!detail || detail === 'noop') return 'nothing needed changing';
  const words: Record<string, string> = {
    tag: 'tagged',
    considering: 'study set',
    owner: 'owner set',
    // Move them to this study as new leads (exportAttach move_new).
    moved: 'opened as a new lead on this study',
    moved_back: 'moved back to this study where they left off',
    reopened: 'reopened',
    kept_committed: 'left as is: in or headed into a study',
    kept_already_on_study: 'left as is: already on this study',
    kept_in_agent_conversation: 'left as is: talking with the texting agent',
    kept_on_outreach_list: 'left as is: on an outreach list',
    kept_changed: 'left as is: their record changed while this ran',
  };
  return detail.split('+').map((a) => words[a] || a).join(', ');
}

/**
 * Why a scheduled message didn't go (crm.scheduled_messages.last_error) in words. The codes come
 * from the send tick (leadClosedReason, gates, the agent, outreach lists); an empty reason on a
 * cancelled row means a person cancelled it.
 */
export function cancelReasonText(lastError: string | null | undefined): string {
  const e = String(lastError || '').trim();
  if (!e) return 'Cancelled by a person';
  if (e === 'contact_missing' || e === 'contact_gone') return 'The lead no longer exists';
  if (e === 'contact_merged') return 'Merged into another record';
  if (e === 'contact_archived') return 'The lead was archived';
  if (e.startsWith('lead_')) {
    const status = e.slice('lead_'.length);
    const label = LIFECYCLES.find((l) => l.value === status)?.label;
    return `The lead was closed${label ? ` (${label})` : ''}`;
  }
  if (e === 'too_many_attempts') return 'Tried too many times without success';
  if (e.startsWith('agent_drop')) return 'The texting agent stopped it';
  if (e.startsWith('blocked:')) {
    const why = e.slice('blocked:'.length);
    if (why === 'opted_out') return 'They opted out of texts';
    if (why === 'consent_revoked') return 'They withdrew their consent';
    if (why === 'suppressed') return 'That email address is blocked';
    if (why === 'no_email') return 'No email address on file';
    return 'Blocked by a sending safety rule';
  }
  if (e === 'outreach_reopen') return 'Stopped when they were added to an outreach list';
  if (e === 'search_export_move') return 'Stopped when a search moved them to another study';
  if (e === 'merged_duplicate_outreach_member') return 'A duplicate record was merged';
  if (e.startsWith('outreach:') || e.startsWith('outreach_')) return 'Stopped by the outreach list';
  if (e.startsWith('deferred:')) return 'Waiting for sending hours';
  if (e.startsWith('timed_out')) return 'Interrupted while sending';
  return 'Stopped by the system';
}
